import { useCallback, useMemo, useState } from "react"
import { services } from "@/services"
import type { AccreditationReceipt } from "@/services/types"
import { AccreditationUnavailableError } from "@/services/accreditation"
import {
  EMPTY_VALUES,
  toRequest,
  toggleId,
  type AccreditationFormValues,
} from "@/features/accreditation/formModel"
import {
  firstErrorField,
  validate,
  type AccreditationErrors,
} from "@/features/accreditation/validation"

export type SubmitStatus = "idle" | "submitting" | "success" | "error"

/** Prefixo unico dos `id` do formulario — usado para rolar ate o primeiro erro. */
export function fieldId(field: keyof AccreditationFormValues): string {
  return `credenciamento-${field}`
}

/** Data local em ISO (AAAA-MM-DD), sem passar por UTC. */
export function todayIso(now: Date = new Date()): string {
  const offsetMs = now.getTimezoneOffset() * 60_000
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10)
}

/**
 * Estado da ficha de credenciamento.
 *
 * Duas decisoes que valem explicacao:
 *
 * 1. Os erros so aparecem depois da primeira tentativa de envio (`showErrors`).
 *    Acusar "campo obrigatorio" no primeiro campo que a pessoa toca, numa ficha
 *    de quarenta campos, e um formulario inteiro em vermelho antes de ela ter
 *    tido chance de preencher. Depois da primeira tentativa, a conferencia
 *    passa a ser continua — ai o vermelho some conforme ela corrige.
 *
 * 2. O envio nao limpa os campos. Se o destino recusar, os dados continuam la
 *    para tentar de novo; perder quarenta campos por um 502 seria imperdoavel.
 *    A limpeza so acontece em `reset`, no botao da tela de confirmacao.
 */
export function useAccreditationForm() {
  const [values, setValues] = useState<AccreditationFormValues>(EMPTY_VALUES)
  const [showErrors, setShowErrors] = useState(false)
  const [status, setStatus] = useState<SubmitStatus>("idle")
  const [receipt, setReceipt] = useState<AccreditationReceipt | null>(null)
  const [submitError, setSubmitError] = useState("")

  const allErrors = useMemo(() => validate(values), [values])
  const errors: AccreditationErrors = showErrors ? allErrors : {}
  const errorCount = Object.keys(allErrors).length

  const setField = useCallback(
    <K extends keyof AccreditationFormValues>(field: K, value: AccreditationFormValues[K]) => {
      setValues((current) => ({ ...current, [field]: value }))
    },
    [],
  )

  const toggleInList = useCallback(
    (field: "services" | "documents", id: string, checked: boolean) => {
      setValues((current) => ({ ...current, [field]: toggleId(current[field], id, checked) }))
    },
    [],
  )

  const focusFirstError = (found: AccreditationErrors) => {
    const field = firstErrorField(found)
    if (!field) return

    const element = document.getElementById(fieldId(field))
    if (!element) return

    // Nas caixas de selecao o `id` esta no input, que fica visualmente
    // escondido: rolar ate ele levaria a tela para o lugar errado. Quem tem
    // posicao na pagina e o rotulo que o embrulha.
    ;(element.closest("label") ?? element).scrollIntoView({ behavior: "smooth", block: "center" })

    // Um `fieldset` nao recebe foco; o primeiro campo dentro dele recebe.
    const focusable =
      element instanceof HTMLFieldSetElement
        ? element.querySelector<HTMLElement>("input, select, textarea")
        : element

    focusable?.focus({ preventScroll: true })
  }

  const submit = async () => {
    const found = validate(values)
    setShowErrors(true)

    if (Object.keys(found).length > 0) {
      setStatus("idle")
      setSubmitError("")
      focusFirstError(found)
      return
    }

    setStatus("submitting")
    setSubmitError("")

    try {
      const result = await services.accreditation.submit(toRequest(values, todayIso()))
      setReceipt(result)
      setStatus("success")
    } catch (cause: unknown) {
      setStatus("error")
      setSubmitError(
        cause instanceof AccreditationUnavailableError
          ? "O envio pelo site ainda não está disponível. Use um dos canais abaixo — os dados que você preencheu continuam nesta página."
          : "Não conseguimos enviar sua ficha agora. Tente novamente em alguns instantes ou fale com a gente pelos canais abaixo.",
      )
    }
  }

  const reset = () => {
    setValues(EMPTY_VALUES)
    setShowErrors(false)
    setStatus("idle")
    setReceipt(null)
    setSubmitError("")
  }

  return {
    values,
    setField,
    toggleInList,
    errors,
    /** Quantos campos ainda impedem o envio — some no resumo acima do botao. */
    errorCount,
    showErrors,
    status,
    receipt,
    submitError,
    submit,
    reset,
  }
}
